import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import "../styles/Journey.css";
import Footer from "../components/Footer";

const navLinks = [
    { to: "/", label: "Home" },
    { to: "#", label: "Our Journey" },
    { to: "/blog", label: "Blogs & Insights" },
    { to: "/get-in-touch", label: "Support" },
  ];

const getDaysSinceLaunch = (launchDateStr) => {
  const launchDate = new Date(launchDateStr);
  const today = new Date();

  // Zero out the time for accurate day comparison
  launchDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const diffInMs = today - launchDate;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  return diffInDays;
};

const categoryMeta = {
  "Launches & Milestones": {
    color: "#8DAAA0",
    emoji: "🚀"
  },
  "Outreach & Events": {
    color: "#328cc1",
    emoji: "📣"
  },
  "Strategic Relationships": {
    color: "#F1BD78",
    emoji: "🤝"
  },
  "Team Learnings": {
    color: "#d98310",
    emoji: "🧠"
  }
};

const timelineData = [
  {
    "date": "June 2025",
    "title": "DataPulse AI joins the Google for Startups Cloud Program",
    "text": "This support from Google marks another step in our journey - helping us build scalable automation on world-class infrastructure, with $25K in Google Cloud credits fueling the next phase of development.",
    "category": "Strategic Relationships",
    "image": "https://www.mydatapulse.co.uk/assets/images/google_cloud_logo.png"
  },
  {
    "date": "June 2025",
    "title": "Patent Filed: Protecting the Vision",
    "text": "DataPulse AI has officially filed its first patent under the product arm of the business. It’s our first “Patent Pending” moment for the team, and a bold signal of the innovation we’re building behind the scenes. More to share soon - but for now, we’re celebrating!",
    "category": "Launches & Milestones",
    "image": "https://www.mydatapulse.co.uk/icons/patent_pending.png"
  },
  {
    "date": "May 2025",
    "title": "DataPulse AI joins the the AWS Activate Program",
    "text": "We’re thrilled to be part of AWS Activate - gaining access to credits, expert training, and startup support to help scale our automation platform and bring our vision to life.",
    "category": "Strategic Relationships",
    "image": "https://www.mydatapulse.co.uk/assets/images/powered_by_aws_2.png"
  },
  {
    "date": "May 2025",
    "title": "DataPulse AI joins the NVIDIA Inception Program",
    "text": "This marks a key milestone in our AI journey - unlocking access to cutting-edge resources, strategic support, and a global community of innovators.",
    "category": "Strategic Relationships",
    "image": "https://www.mydatapulse.co.uk/assets/images/nvidia-inception-badge.png"
  },
  {
    "date": "May 2025",
    "title": "Attended King’s Festival of Artificial Intelligence",
    "text": "Attended the King’s College AI Festival. A refreshing step back to reflect on ethics, impact, and where innovation meets responsibility.",
    "category": "Team Learnings",
    "image": "https://www.mydatapulse.co.uk/assets/images/kings_college_london_logo.jpg"
  },
  {
    "date": "May 2025",
    "title": "Trademark Filed",
    "text": "Trademark filing submitted for DataPulse AI - part of laying legal groundwork to build something defensible and enduring.",
    "category": "Launches & Milestones",
    "image": "https://www.mydatapulse.co.uk/assets/images/trademark_symbol.png"
  },
  {
    "date": "May 2025",
    "title": "First Product Prototype Complete",
    "text": "The first prototype of The Digital Connect by DataPulse AI is completed. While services fund early execution, the SaaS product vision is what defines DataPulse AI's longer term vision.",
    "category": "Launches & Milestones",
    "image": "https://www.mydatapulse.co.uk/assets/images/DiscoverySessionImg.png"
  },
  {
    "date": "May 2025",
    "title": "Attended AI & Digital Transformation 2025",
    "text": "Took part in Roar Media’s AI conference. Valuable conversations around real-world applications, adoption barriers, and where the ecosystem is heading.",
    "category": "Outreach & Events",
    "image": "https://www.mydatapulse.co.uk/assets/images/digital_transformation_conference.png"
  },
  {
    "date": "May 2025",
    "title": "Launched Blogs & Insights",
    "text": "Launched the DataPulse AI Blog: a space to think out loud, share hard-earned lessons, and document the build - for ourselves and others walking similar paths.",
    "category": "Launches & Milestones",
    "image": "https://www.mydatapulse.co.uk/assets/images/blog-icon.png"
  },
  {
    "date": "May 2025",
    "title": "DataPulse AI Officially Launched",
    "text": "We publicly launched DataPulse AI on LinkedIn and beyond - introducing our mission, sharing our vision, and opening our doors to the world.",
    "category": "Launches & Milestones",
    "image": "https://www.mydatapulse.co.uk/icons/logo-head.png"
  },
  {
    "date": "April 2025",
    "title": "DataPulse AI Limited is Registered",
    "text": "DataPulse AI becomes a registered entity - a foundational moment which started our journey of transforming a vision into a reality.",
    "category": "Launches & Milestones",
    "image": "https://www.mydatapulse.co.uk/assets/images/company_house_image.png"
  }
];

function Timeline() {
  const allCategories = Array.from(new Set(timelineData.map(item => item.category)));
  const [activeCategory, setActiveCategory] = useState("All");
  const daysSinceLaunch = getDaysSinceLaunch("2025-05-12");

  const handleSelectCategory = (category) => {
    setActiveCategory(category);
  };

  const isAllActive = activeCategory === "All";

  const filteredData =
    isAllActive
      ? timelineData
      : timelineData.filter(item => item.category === activeCategory);

  return (
    <>
      <Helmet>
            <title>Our Journey | DataPulse AI 🚀</title>
            <meta property="og:title" content="Our Journey | DataPulse AI 🚀" />
            <meta name="robots" content="index, follow"/>
            <meta property="og:description" content="Follow the milestones, partnerships, and growth of DataPulse AI - from product launches to industry events." />
            <meta property="og:url" content="https://www.mydatapulse.co.uk/journey" />
            <meta name="description" content="Track our journey as DataPulse AI builds a smarter future with automation, product innovation, and startup momentum." />


            {/* Structured Data: Organization */}
            <script type="application/ld+json">
              {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "DataPulse AI",
              "url": "https://www.mydatapulse.co.uk",
              "logo": "https://www.mydatapulse.co.uk/logo-clean-large.png",
              "description": "Free up time and scale your business smarter with automation and AI - fully managed by DataPulse AI. No hiring, no tech hassle, no learning curve. Just results.",
              "sameAs": [
                  "https://www.linkedin.com/company/mydatapulse",
              ]
              })}
            </script>
      </Helmet>

      <div className="min-h-screen w-screen overflow-hidden flex flex-col">
        <div id="header" className="z-10 bg-white">
          <Header links={navLinks} />
        </div>

        <section id="timeline" className="flex-1 bg-white flex items-start justify-center pt-10">
          <div className="max-w-screen-xl px-4 text-center">
            <h2 className="mb-1 text-3xl font-extrabold tracking-tight">Our Journey so Far</h2>
            <p className="mb-4 font-light text-gray-500 sm:text-xl">
              We’ve come a long way - and we’re just getting started.
              <span className="text-purple-700"><br/>Days since launch: <strong>{daysSinceLaunch}</strong></span>
            </p>

            <div className="flex flex-wrap gap-4 justify-center mb-6">
              <button
                onClick={() => handleSelectCategory("All")}
                className={`px-4 py-2 text-sm font-medium rounded-full border transition ${
                  isAllActive
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200"
                }`}
              >
                All
              </button>

              {allCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleSelectCategory(category)}
                  className={`px-4 py-2 text-sm font-medium rounded-full border transition ${
                    activeCategory === category
                      ? "bg-purple-600 text-white border-purple-600"
                      : "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200"
                  }`}
                >
                  <span
                    className="font-extrabold"
                    style={{ color: categoryMeta[category]?.color || "#6514a7" }}
                  >
                    O
                  </span> {category}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-center mb-4">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="text-gray-500 sm:text-xlmx-4 text-xl font-extrabold">
              📍 Today
            </span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

            {/* Timeline Items */}
            <div className="timeline">
              {filteredData.map((item, index) => (
                <div
                  key={index}
                  className={`container ${index % 2 === 0 ? "left" : "right"}`}
                >
                  <span
                    className="timeline-dot"
                    style={{ borderColor: categoryMeta[item.category]?.color || "#6514a7" }}
                  />
                  <div className="content text-center">
                    {item.image && (
                      <img className="h-15 max-w-50pct max-w-xs mx-auto mb-2 object-contain h-auto" src={item.image} alt={item.date} />
                    )}
                    <p className="text-lg font-bold text-title-purple y-0">{item.title}</p>
                    <p>{item.text}</p>
                    <p className="text-blue-600 font-bold">{item.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <Footer />

    </>
  );
}

export default Timeline;
